import {useForceRemountOnChange} from '@wandb/weave/common/util/hooks';
import {useCallback, useMemo} from 'react';

import * as Filters from '../../../util/filters';
import {EMPTY_FILTER_VALUE} from '../../../util/filtersDefaults';
import type * as FilterTypes from '../../../util/filterTypes';
import * as Run from '../../../util/runs';
import type * as RunTypes from '../../../util/runTypes';
import {tryJSONParse} from '../../../util/try_json_parse';
import {getCurrentSelections} from '../../InfiniteScrollDropdown/utils';

type RunFilter = FilterTypes.IndividualFilter<RunTypes.Key>;

export function useRunFilterValueDropdown({
  filter,
  setFilter,
}: {
  filter: RunFilter;
  setFilter: (filter: RunFilter) => void;
}) {
  const keyPath = Run.keyToServerPath(filter.key);
  const extraQueryVariables = useMemo(() => ({keyPath}), [keyPath]);
  const isMultiSelect = Filters.isMultiValue(filter);
  const selections = getCurrentSelections(filter, []);

  // We need to reset the state of the infinite dropdown when the filter key changes.
  // Otherwise, the results from the previous key persist.
  // TODO: Do this, but like, better
  const {shouldRenderNull} = useForceRemountOnChange([filter.key]);

  const toggleSelection = useCallback(
    (newSelection: string) => {
      const selectionSet = new Set(getCurrentSelections(filter));
      const isSelected = selectionSet.has(newSelection);
      const isSingleValue = !Filters.isMultiValue(filter);

      if (isSingleValue && isSelected) {
        setFilter({...filter, value: EMPTY_FILTER_VALUE, disabled: true});
        return;
      }

      if (isSingleValue) {
        setFilter({
          ...filter,
          value: tryJSONParse(newSelection),
          disabled: false,
        });
        return;
      }

      if (isSelected) {
        selectionSet.delete(newSelection);
      } else {
        selectionSet.add(newSelection);
      }
      setFilter({
        ...filter,
        value: [...selectionSet].map(tryJSONParse),
        disabled: false,
      });
    },
    [filter, setFilter]
  );

  return {
    keyPath,
    extraQueryVariables,
    isMultiSelect,
    selections,
    toggleSelection,
    shouldRenderNull,
  };
}
